// FoldedChips: a search's clauses as a row of chips, as many of the newest
// in sight as the row has room for and the oldest folded behind "+N", as the
// sidebar's search box and the board's bar hold theirs. "+N" lists what it
// folds under `anchor`, each clause with its field, to take out there. The
// newest gives way where it alone is wider than the row, cut short with its
// close in reach. Until the row is measured, nothing folds.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EYEBROW_CLASS } from "../lib/ui.js";

const LIST_W = 320;
const UNMEASURED = Object.freeze({ more: 0, chips: Object.freeze([]) });

// How many of the oldest fold: the newest in sight, as many as `room` holds
// beside what follows them and "+N", a pixel over each chip's measure so one
// that fits is never cut short. Unmeasured, none.
function foldedOf({ room, after, more, chips }, gap, hasAfter) {
  if (!(room > 0)) return 0;
  const fits = room - (hasAfter ? after + gap : 0);
  let used = -gap;
  let kept = 0;
  for (let j = chips.length - 1; j >= 0; j -= 1) {
    const w = chips[j] + 1 + gap;
    if (kept > 0 && used + w + (j > 0 ? more + gap : 0) > fits) break;
    used += w;
    kept += 1;
  }
  return chips.length - kept;
}

// What "+N" folds, listed as the finder lists what its own "+N" folds. Put
// away by a click anywhere else, Escape, or the window resized. A click in
// it stays in it: drawn over the page, it is still the row's child, and
// what holds the row (the sidebar's box opens the finder) must not hear it.
function Folded({ at, items, toggle, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const down = (e) => { if (!ref.current?.contains(e.target) && !toggle.current?.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("keydown", key);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("keydown", key);
      window.removeEventListener("resize", onClose);
    };
  }, [toggle, onClose]);
  return createPortal(
    <div
      ref={ref}
      className="fixed z-[850] flex max-h-[360px] flex-col overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-lg"
      style={{ top: at.top + 6, left: at.left, width: LIST_W }}
      onClick={(e) => e.stopPropagation()}
      data-folded-list=""
    >
      <div className={`${EYEBROW_CLASS} px-[12px] pb-[2px] pt-[9px]`}>Also in the search</div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-[4px]">
        {items.map((x) => (
          <div key={x.key} className="flex items-center gap-[8px] px-[12px] text-[12px] leading-[28px]">
            <span className="min-w-0 flex-1 truncate text-[color:var(--ink)]">
              {x.negated ? <span className="text-[color:var(--muted)]">{"not "}</span> : null}
              {x.field ? <span className="text-[color:var(--muted)]">{`${x.field} `}</span> : null}
              {x.value}
            </span>
            <button type="button" aria-label={x.dropLabel} onClick={x.drop} className="shrink-0 text-[14px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]">&times;</button>
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}

// `items`: the clauses, oldest first, each { key, field, negated, value,
// drop, dropLabel }; `chip(item, fit)`: one drawn, keyed by its key, `fit`
// its flex class; `moreClassName`: how "+N" is drawn; `anchor`: what the
// list hangs under; `gap`: the room between chips; `after`: what follows
// them and never folds (the board's picks).
export default function FoldedChips({ items, chip, moreClassName, anchor, gap = 4, after = null }) {
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const afterRef = useRef(null);
  const moreRef = useRef(null);
  // The sizes the fold is worked out from, kept out of state: a column
  // dragged wider or narrower draws the row again only where the number
  // folded changes.
  const sizes = useRef({ ...UNMEASURED, room: 0, after: 0 });
  const [count, setCount] = useState(0);
  // Where the list hangs, while "+N" lists what it folds.
  const [listed, setListed] = useState(null);
  const putAway = useCallback(() => setListed(null), []);
  const hasAfter = after != null;
  const refold = useCallback(() => setCount(foldedOf(sizes.current, gap, hasAfter)), [gap, hasAfter]);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const read = () => {
      sizes.current.room = row.clientWidth;
      sizes.current.after = afterRef.current?.offsetWidth || 0;
      refold();
    };
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(row);
    if (afterRef.current) ro.observe(afterRef.current);
    return () => ro.disconnect();
  }, [refold]);
  // Each chip as wide as it is drawn, and "+N" at its widest, measured out
  // of sight.
  const shape = JSON.stringify(items.map((x) => x.key));
  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const [more, ...chips] = [...el.children].map((c) => c.offsetWidth);
    sizes.current.more = more || 0;
    sizes.current.chips = chips;
    refold();
  }, [shape, refold]);
  // Until the fold is worked out again for a change of clauses, never more
  // than leaves the newest.
  const folded = Math.min(count, Math.max(0, items.length - 1));
  useEffect(() => {
    if (!folded) setListed(null);
  }, [folded]);
  return (
    <span ref={rowRef} className="relative flex min-w-0 flex-1 items-center overflow-hidden" style={{ gap }} data-folded-chips="">
      <span ref={measureRef} className="pointer-events-none invisible absolute left-0 top-0 flex" aria-hidden="true" inert>
        <span className="flex" data-measure="more"><span className={moreClassName}>{`+${items.length}`}</span></span>
        {items.map((x) => <span key={x.key} className="flex" data-measure="chip">{chip(x, "shrink-0")}</span>)}
      </span>
      {folded ? (
        <button
          ref={moreRef}
          type="button"
          aria-label={`${folded} more in the search`}
          aria-expanded={!!listed}
          onClick={(e) => {
            e.stopPropagation();
            const left = e.currentTarget.getBoundingClientRect().left;
            setListed((v) => (v ? null : { top: (anchor?.current || rowRef.current).getBoundingClientRect().bottom, left }));
          }}
          className={moreClassName}
          data-folded-more=""
        >
          {`+${folded}`}
        </button>
      ) : null}
      {items.map((x, i) => (i < folded ? null : chip(x, i === items.length - 1 ? "shrink" : "shrink-0")))}
      {hasAfter ? <span ref={afterRef} className="flex shrink-0" data-folded-after="">{after}</span> : null}
      {listed && folded ? <Folded at={listed} items={items.slice(0, folded)} toggle={moreRef} onClose={putAway} /> : null}
    </span>
  );
}
